import { useQuery } from '@apollo/client'
import { Icon, usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Table } from '@/components_deprecated'
import { ALL_FACILITIES_QUERY } from '@/modules/companies/queries'
import type { FacilityNode } from '@/modules/companies/types.graphql'
import { useConfirm } from '@/utils/hooks'
import { OldAddButton, Header, OldSection, SectionHeader } from '../common'
import type { FacilityForm } from './FacilityPrompt'
import { FacilityPrompt } from './FacilityPrompt'
import { useFacilityMutations } from './hooks'
import { getNodes } from '@/utils/graphql'

export const FacilitySettings: React.JSX.CFC = () => {
  const translations = useTranslate({
    prompt: {
      delete: ['settings.prompts.delete', { name: '' }],
      deleteTitle: 'settings.prompts.facility-delete-title',
    },
    externalId: 'common.id',
    facilities: 'common.facilities',
    name: 'common.name',
  })

  const addPrompt = usePrompt()
  const confirm = useConfirm()
  const mutations = useFacilityMutations()

  const { data } = useQuery(ALL_FACILITIES_QUERY)

  const facilities = getNodes(data?.allFacilities)

  async function handleCreateFacility() {
    const { data } = await addPrompt<FacilityForm | null>(resolve => (
      <FacilityPrompt existingFacilities={facilities} onSubmit={resolve} />
    ))
    if (data === null) return

    void mutations.createFacility({
      variables: {
        input: data,
      },
    })
  }

  async function handlePatchFacility(facility: FacilityNode) {
    const { data } = await addPrompt<FacilityForm | null>(resolve => (
      <FacilityPrompt
        initialData={facility}
        existingFacilities={facilities}
        onSubmit={resolve}
      />
    ))
    if (data === null) return

    void mutations.patchFacility({
      variables: {
        id: facility.id,
        input: {
          externalId: data.externalId,
          name: data.name,
        },
      },
    })
  }

  async function handleDeleteFacility(facility: FacilityNode) {
    const { data: answer } = await confirm(
      translations.prompt.delete({ name: facility.name }),
      translations.prompt.deleteTitle
    )
    if (!answer) return

    void mutations.deleteFacility({
      variables: {
        id: facility.id,
      },
    })
  }

  return (
    <OldSection>
      <Header>
        <SectionHeader>{translations.facilities}</SectionHeader>
        <OldAddButton adding={false} onClick={handleCreateFacility} />
      </Header>

      <Table
        noShadow
        noBorders
        noHeaderMargin
        noRowMargin
        rowHoverColor="secondary"
      >
        <thead>
          <tr>
            <th>{translations.name}</th>
            <th colSpan={2}>{translations.externalId}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {facilities.map(facility => (
            <tr key={facility.id}>
              <td>{facility.name}</td>
              <td>{facility.externalId ?? '-'}</td>

              <td width="1px">
                <Icon
                  icon="edit"
                  cursor="pointer"
                  color="gray6"
                  hoverColor="secondary"
                  onClick={() => handlePatchFacility(facility)}
                />
              </td>

              <td width="1px">
                <Icon
                  icon="times"
                  cursor="pointer"
                  color="gray6"
                  hoverColor="red"
                  onClick={() => handleDeleteFacility(facility)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </OldSection>
  )
}
